import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Alert,
} from '@mui/material';
import { useCreateNode } from '../hooks/useNodes';

interface Props {
  open: boolean;
  onClose: () => void;
}

// 协议别名映射
const PROTO_MAP: Record<string, string> = {
  vmess: 'vmess', vless: 'vless', trojan: 'trojan',
  ss: 'shadowsocks', shadowsocks: 'shadowsocks',
  socks: 'socks', http: 'http',
};

// 根据节点名和地址自动识别地区
function guessRegion(name: string, address: string): string {
  const text = (name + ' ' + address).toLowerCase();
  const map: [RegExp, string][] = [
    [/(?:^|[-\s])(?:us|usa|united.?states|america)(?:[-\s]|$)/, '美国'],
    [/(?:^|[-\s])(?:jp|japan|tokyo|osaka)(?:[-\s]|$)/, '日本'],
    [/(?:^|[-\s])(?:hk|hong.?kong)(?:[-\s]|$)/, '香港'],
    [/(?:^|[-\s])(?:sg|singapore)(?:[-\s]|$)/, '新加坡'],
    [/(?:^|[-\s])(?:tw|taiwan|taipei)(?:[-\s]|$)/, '台湾'],
    [/(?:^|[-\s])(?:kr|korea|seoul)(?:[-\s]|$)/, '韩国'],
    [/(?:^|[-\s])(?:de|germany|frankfurt)(?:[-\s]|$)/, '德国'],
    [/(?:^|[-\s])(?:uk|gb|england|london)(?:[-\s]|$)/, '英国'],
    [/(?:^|[-\s])(?:nl|netherlands|amsterdam)(?:[-\s]|$)/, '荷兰'],
    [/(?:^|[-\s])(?:fr|france|paris)(?:[-\s]|$)/, '法国'],
    [/(?:^|[-\s])(?:ca|canada|toronto)(?:[-\s]|$)/, '加拿大'],
    [/(?:^|[-\s])(?:au|australia|sydney)(?:[-\s]|$)/, '澳大利亚'],
    [/(?:^|[-\s])(?:in|india|mumbai)(?:[-\s]|$)/, '印度'],
    [/(?:^|[-\s])(?:ru|russia|moscow)(?:[-\s]|$)/, '俄罗斯'],
    [/(?:^|[-\s])(?:cn|china|beijing|shanghai)(?:[-\s]|$)/, '中国'],
  ];
  for (const [re, region] of map) {
    if (re.test(text)) return region;
  }
  return '';
}

// 解析 VMess 链接
function parseVmessLink(link: string): Record<string, any> | null {
  try {
    const b64 = link.replace(/^vmess:\/\//, '');
    const json = JSON.parse(atob(b64));
    return {
      name: json.ps || '',
      protocol: 'vmess',
      address: json.add || '',
      port: Number(json.port) || 0,
      region: guessRegion(json.ps || '', json.add || ''),
      configJson: {
        uuid: json.id || '',
        aid: Number(json.aid) || 0,
        security: json.scy || 'auto',
        network: json.net || 'tcp',
        type: json.type || 'none',
        host: json.host || '',
        path: json.path || '',
        tls: json.tls || 'none',
        sni: json.sni || '',
        alpn: json.alpn || '',
        fp: json.fp || '',
      },
    };
  } catch {
    return null;
  }
}

// 解析其他协议的 share link
function parseShareLink(link: string): Record<string, any> | null {
  try {
    const url = new URL(link);
    const protoKey = url.protocol.replace(/:$/, '');
    const protocol = PROTO_MAP[protoKey] || protoKey;

    // 去掉协议名后剩余部分: userinfo@host:port?params#name
    const userinfo = decodeURIComponent(url.username || '');
    const host = url.hostname || '';
    const port = Number(url.port) || 443;
    const name = decodeURIComponent(url.hash?.replace('#', '') || '') || host;
    const params: Record<string, string> = {};
    url.searchParams.forEach((v, k) => params[k] = v);

    // 构建 configJson
    const configJson: Record<string, any> = {};
    if (protocol === 'trojan') {
      configJson.password = userinfo;
      configJson.sni = params.sni || host;
    } else if (protocol === 'shadowsocks') {
      const [method, password] = atob(userinfo).split(':');
      configJson.method = method;
      configJson.password = password;
    } else if (protocol === 'vless') {
      configJson.uuid = userinfo;
      configJson.encryption = params.encryption || 'none';
      configJson.flow = params.flow || '';
      configJson.network = params.type || 'tcp';
      configJson.security = params.security || 'none';
      configJson.sni = params.sni || '';
    }

    return { name, protocol, address: host, port, region: guessRegion(name, host), configJson };
  } catch {
    return null;
  }
}

export function ImportNodeDialog({ open, onClose }: Props) {
  const createNode = useCreateNode();
  const [link, setLink] = useState('');
  const [parsed, setParsed] = useState<Record<string, any> | null>(null);
  const [error, setError] = useState('');

  const handleParse = () => {
    setError('');
    setParsed(null);
    const trimmed = link.trim();
    if (!trimmed) return;

    // 尝试 VMess
    if (trimmed.startsWith('vmess://')) {
      const result = parseVmessLink(trimmed);
      if (result) { setParsed(result); return; }
    }

    // 尝试其他协议
    const result = parseShareLink(trimmed);
    if (result) { setParsed(result); return; }

    setError('无法解析该链接，请检查格式。支持的协议：VMess、VLESS、Trojan、Shadowsocks');
  };

  const handleImport = async () => {
    if (!parsed) return;
    try {
      await createNode.mutateAsync({
        name: parsed.name,
        protocol: parsed.protocol,
        address: parsed.address,
        port: parsed.port,
        region: parsed.region,
        configJson: parsed.configJson,
      });
      setLink('');
      setParsed(null);
      onClose();
    } catch (e: any) {
      setError(e?.message || '导入失败');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>导入节点</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            粘贴 VMess/VLESS/Trojan/Shadowsocks 分享链接，自动识别并添加
          </Typography>

          <TextField
            label="节点链接"
            value={link}
            onChange={e => { setLink(e.target.value); setParsed(null); setError(''); }}
            multiline
            rows={6}
            fullWidth
            placeholder={`vmess://ewogICJ2Ijog...\nvless://uuid@host:port?...\ntrojan://password@host:port?...`}
            autoFocus
          />

          {error && <Alert severity="error" sx={{ wordBreak: 'break-all' }}>{error}</Alert>}

          {parsed && (
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>识别结果：</Typography>
              <Typography variant="body2">名称：{parsed.name}</Typography>
              <Typography variant="body2">协议：{parsed.protocol.toUpperCase()}</Typography>
              <Typography variant="body2">地址：{parsed.address}</Typography>
              <Typography variant="body2">端口：{parsed.port}</Typography>
              {parsed.configJson && (
                <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontSize: 12 }}>
                  配置：{JSON.stringify(parsed.configJson).substring(0, 100)}...
                </Typography>
              )}
            </Box>
          )}

          <Button
            variant="outlined"
            onClick={handleParse}
            disabled={!link.trim()}
            fullWidth
          >
            解析链接
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!parsed || createNode.isPending}
        >
          {createNode.isPending ? '导入中...' : '导入节点'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
